import {
  computed,
  type ComputedRef,
  inject,
  type MaybeRefOrGetter,
  provide,
  toValue,
} from 'vue';
import {
  InsightDateKey,
  InsightIdKey,
  InsightPageNameKey,
  type InsightTrackingContext,
  InsightTrackingContextKey,
} from '@/components/home/insights/injectionKeys';
import type {
  InsightAxisType,
  InsightTrackingPageName,
} from '@/types/insights';

export const provideInsightId = (id: MaybeRefOrGetter<number | null>) => {
  provide(
    InsightIdKey,
    computed(() => toValue(id)),
  );
};

export const useInsightId = (): number | null => {
  const insightId = inject(InsightIdKey);
  return insightId?.value ?? null;
};

export const provideInsightDate = (date: MaybeRefOrGetter<string | null>) => {
  provide(
    InsightDateKey,
    computed(() => toValue(date)),
  );
};

export const useInsightDate = (): ComputedRef<string | null> =>
  inject(
    InsightDateKey,
    computed(() => null),
  );

export const provideInsightPageName = (pageName: InsightTrackingPageName) => {
  provide(InsightPageNameKey, pageName);
};

export const useInsightPageName = (): InsightTrackingPageName => {
  const pageName = inject(InsightPageNameKey);
  if (!pageName) {
    throw new Error(
      'useInsightPageName は provideInsightPageName を呼んだコンポーネントの配下で使用してください',
    );
  }
  return pageName;
};

export const provideInsightTracking = (context: InsightTrackingContext) => {
  provide(InsightTrackingContextKey, context);
};

export const provideInsightAxisTracking = (axis: {
  id: number;
  name: InsightAxisType;
}) => {
  // パネルは insight が processed（id 確定）のときのみ描画されるため、実質 null にはならない
  const insightId = useInsightId() ?? 0;
  provideInsightTracking({
    pageName: useInsightPageName(),
    insightId,
    axisId: axis.id,
    axisName: axis.name,
  });
};

export const useInsightTracking = (): InsightTrackingContext => {
  const context = inject(InsightTrackingContextKey);
  if (!context) {
    throw new Error(
      'useInsightTracking は insight-axis-panel の配下で使用してください',
    );
  }
  return context;
};
